import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  IoStatsChartOutline,
  IoTrendingUpOutline,
  IoMedkitOutline,
  IoCheckmarkCircleOutline,
  IoTimeOutline,
  IoArrowBack,
  IoPersonOutline,
} from 'react-icons/io5';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Highlights from '../common/Highlights';

const API_BASE_URL = "https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/";

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const AccountSummary = ({ onBack }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  
  // ✅ NEW: Sales rep specific states
  const [userRole, setUserRole] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [loadingProviders, setLoadingProviders] = useState(false);

  useEffect(() => {
    // Get user role from localStorage
    const role = localStorage.getItem('userRole');
    setUserRole(role);
    
    // If sales rep, fetch providers list
    if (role === 'sales_rep') {
      fetchProviders();
    } else {
      loadStats();
    }
  }, []);

  useEffect(() => {
    if (userRole) {
      loadStats();
    }
  }, [selectedPeriod, selectedProvider, userRole]);

  // ✅ NEW: Fetch providers for sales rep
  const fetchProviders = async () => {
    setLoadingProviders(true);
    try {
      const response = await fetch(`${API_BASE_URL}/sales-rep/providers/?access_only=true`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setProviders(data.data || []);
        // Auto-select first provider if available
        if (data.data && data.data.length > 0) {
          setSelectedProvider(data.data[0].id.toString());
        }
      }
    } catch (err) {
      console.error('Error loading providers:', err);
    } finally {
      setLoadingProviders(false);
      loadStats(); // Load stats after providers are fetched
    }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      // ✅ UPDATED: Different endpoints for provider vs sales rep
      let url;
      if (userRole === 'sales_rep') {
        if (selectedProvider === 'all') {
          // Get aggregated stats for all providers
          url = `${API_BASE_URL}/sales-rep/stats/?period=${selectedPeriod}`;
        } else {
          // Get stats for specific provider
          url = `${API_BASE_URL}/sales-rep/stats/?period=${selectedPeriod}&provider_id=${selectedProvider}`;
        }
      } else {
        // Provider viewing their own stats
        url = `${API_BASE_URL}/stats/?period=${selectedPeriod}`;
      }

      const response = await fetch(url, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const periods = [
    { id: 'all', label: 'All Time' },
    { id: 'year', label: 'Last Year' },
    { id: '6months', label: 'Last 6 Months' },
    { id: '3months', label: 'Last 3 Months' },
    { id: 'month', label: 'Last Month' },
  ];

  // Colors for charts
  const COLORS = {
    conservative: '#14b8a6', // teal
    advanced: '#8b5cf6',     // purple
    primary: '#0ea5e9',      // sky blue
  };

  // Prepare data for pie chart
  const pieData = stats ? [
    { name: 'Conservative Care', value: stats.by_care_type.conservative, color: COLORS.conservative },
    { name: 'Advanced Care', value: stats.by_care_type.advanced, color: COLORS.advanced },
  ] : [];

  if (loading || (userRole === 'sales_rep' && loadingProviders)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-500 border-t-transparent mx-auto mb-4"></div>
          </div>
          <p className="text-gray-600 dark:text-gray-400 font-semibold">
            {loadingProviders ? 'Loading providers...' : 'Loading statistics...'}
          </p>
        </motion.div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-400">Failed to load statistics</p>
        <button onClick={loadStats} className="mt-4 px-6 py-2 bg-teal-500 text-white rounded-lg">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="w-full bg-gradient-to-br from-gray-50 via-teal-50/30 to-blue-50/20 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 pb-8">
      <div className="max-w-[95%] xl:max-w-[98%] mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {/* Header */}
        <div className="mb-8">
          <motion.button
            onClick={onBack}
            className="flex items-center gap-2 text-teal-600 dark:text-teal-400 hover:gap-3 transition-all font-bold mb-6 group"
            whileHover={{ x: -5 }}
          >
            <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" />
            Back to Dashboard
          </motion.button>

          <div className="flex flex-col gap-4">
            {/* Title Row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Account Summary</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {userRole === 'sales_rep' 
                    ? 'View statistics for your assigned providers' 
                    : 'View your order statistics and trends'}
                </p>
              </div>

              {/* Period Filter */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {periods.map((period) => (
                  <button
                    key={period.id}
                    onClick={() => setSelectedPeriod(period.id)}
                    className={`px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-all ${
                      selectedPeriod === period.id
                        ? 'bg-teal-500 text-white shadow-lg'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ✅ NEW: Provider Selector for Sales Reps */}
            {userRole === 'sales_rep' && providers.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-white via-teal-50/20 to-blue-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-xl shadow-md border-2 border-teal-200/30 dark:border-teal-800/30 p-4"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    <IoPersonOutline className="text-lg text-teal-600 dark:text-teal-400" />
                    Select Provider:
                  </label>
                  <select
                    value={selectedProvider}
                    onChange={(e) => setSelectedProvider(e.target.value)}
                    className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white font-medium focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                  >
                    <option value="all">All Providers (Aggregated)</option>
                    {providers.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name} - {provider.facility || provider.email}
                      </option>
                    ))}
                  </select>
                  <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {providers.length} provider{providers.length !== 1 ? 's' : ''} with access
                  </div>
                </div>

                {/* Selected Provider Info */}
                {selectedProvider !== 'all' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
                  >
                    {(() => {
                      const provider = providers.find(p => p.id.toString() === selectedProvider);
                      return provider ? (
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                            {provider.name?.charAt(0) || 'P'}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 dark:text-white">{provider.name}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{provider.email}</p>
                            {provider.facility && (
                              <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                🏥 {provider.facility}
                              </p>
                            )}
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </motion.div>
                )}
              </motion.div>
            )}

            {/* ✅ No Providers Warning for Sales Rep */}
            {userRole === 'sales_rep' && providers.length === 0 && !loadingProviders && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4"
              >
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong className="font-semibold">No providers with access:</strong> You don't have access to any provider data yet. Ask your assigned providers to grant you access.
                </p>
              </motion.div>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Orders Card */}
          <motion.div
            className="bg-gradient-to-br from-white via-teal-50/20 to-blue-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl shadow-md border-2 border-teal-200/30 dark:border-teal-800/30 p-6 hover:shadow-lg transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <IoStatsChartOutline className="text-2xl text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">TOTAL</span>
            </div>
            <h3 className="text-4xl font-black text-gray-900 dark:text-white mb-1">
              {stats.total_orders}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Total Orders</p>
          </motion.div>

          {/* Conservative Care Card */}
          <motion.div
            className="bg-gradient-to-br from-white via-teal-50/20 to-blue-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl shadow-md border-2 border-teal-200/30 dark:border-teal-800/30 p-6 hover:shadow-lg transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center">
                <IoMedkitOutline className="text-2xl text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">CONSERVATIVE</span>
            </div>
            <h3 className="text-4xl font-black text-gray-900 dark:text-white mb-1">
              {stats.by_care_type.conservative}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Conservative Care</p>
          </motion.div>

          {/* Advanced Care Card */}
          <motion.div
            className="bg-gradient-to-br from-white via-teal-50/20 to-blue-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl shadow-md border-2 border-teal-200/30 dark:border-teal-800/30 p-6 hover:shadow-lg transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <IoTrendingUpOutline className="text-2xl text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">ADVANCED</span>
            </div>
            <h3 className="text-4xl font-black text-gray-900 dark:text-white mb-1">
              {stats.by_care_type.advanced}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Advanced Care</p>
          </motion.div>

          {/* Completed Orders Card */}
          <motion.div
            className="bg-gradient-to-br from-white via-teal-50/20 to-blue-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl shadow-md border-2 border-teal-200/30 dark:border-teal-800/30 p-6 hover:shadow-lg transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                <IoCheckmarkCircleOutline className="text-2xl text-white" />
              </div>
              <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">DELIVERED</span>
            </div>
            <h3 className="text-4xl font-black text-gray-900 dark:text-white mb-1">
              {stats.by_status.delivered || 0}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Completed Orders</p>
          </motion.div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Orders Over Time - Line Chart */}
          <motion.div
            className="bg-gradient-to-br from-white via-teal-50/20 to-blue-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl shadow-md border-2 border-teal-200/30 dark:border-teal-800/30 p-6 hover:shadow-lg transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Highlights emoji="📈" color="purple" variant="bold" inline className="mb-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Orders Over Time
              </h3>
            </Highlights>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats.orders_over_time}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="period"
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  stroke="#9ca3af"
                />
                <YAxis tick={{ fill: '#6b7280', fontSize: 12 }} stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '8px',
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="conservative"
                  stroke={COLORS.conservative}
                  strokeWidth={3}
                  name="Conservative Care"
                  dot={{ fill: COLORS.conservative, r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="advanced"
                  stroke={COLORS.advanced}
                  strokeWidth={3}
                  name="Advanced Care"
                  dot={{ fill: COLORS.advanced, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Care Type Distribution - Pie Chart */}
          <motion.div
            className="bg-gradient-to-br from-white via-teal-50/20 to-blue-50/10 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-2xl shadow-md border-2 border-teal-200/30 dark:border-teal-800/30 p-6 hover:shadow-lg transition-shadow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="text-2xl">🥧</span>
              Care Type Distribution
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-6 space-y-3">
              {pieData.map((item) => (
                <div key={item.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {item.name}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {item.value} orders
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Status Breakdown */}
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <Highlights emoji="📊" color="green" variant="bold" inline className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Order Status Breakdown
            </h3>
          </Highlights>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { key: 'pending', label: 'Pending', color: 'amber', icon: <IoTimeOutline /> },
              { key: 'processing', label: 'Processing', color: 'blue', icon: <IoStatsChartOutline /> },
              { key: 'shipped', label: 'Shipped', color: 'indigo', icon: <IoTrendingUpOutline /> },
              { key: 'delivered', label: 'Delivered', color: 'green', icon: <IoCheckmarkCircleOutline /> },
              { key: 'cancelled', label: 'Cancelled', color: 'red', icon: <IoCheckmarkCircleOutline /> },
            ].map((status) => (
              <div
                key={status.key}
                className={`p-4 rounded-xl border-2 border-${status.color}-200 dark:border-${status.color}-800 bg-${status.color}-50 dark:bg-${status.color}-900/20`}
              >
                <div className={`text-2xl text-${status.color}-600 dark:text-${status.color}-400 mb-2`}>
                  {status.icon}
                </div>
                <div className={`text-3xl font-black text-${status.color}-700 dark:text-${status.color}-300 mb-1`}>
                  {stats.by_status[status.key] || 0}
                </div>
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  {status.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AccountSummary;